/** Growing token store with a folded-form intern table (ENGINE-SPEC §2.1, §2.5, §11.2). 11 bytes per token. */
import type { Tokens } from '../document/types.ts';
import type { TokenBuffer } from './types.ts';

export class TokenStore implements TokenBuffer {
	count = 0;
	start: Uint32Array;
	len: Uint16Array;
	id: Uint32Array;
	cls: Uint8Array;
	private cap: number;
	private readonly vocab: string[] = [];
	private readonly map = new Map<string, number>();

	constructor(capacity = 1024) {
		this.cap = Math.max(16, capacity);
		this.start = new Uint32Array(this.cap);
		this.len = new Uint16Array(this.cap);
		this.id = new Uint32Array(this.cap);
		this.cls = new Uint8Array(this.cap);
	}

	get vocabSize(): number {
		return this.vocab.length;
	}

	private grow(): void {
		const cap = this.cap * 2;
		const start = new Uint32Array(cap);
		start.set(this.start);
		const len = new Uint16Array(cap);
		len.set(this.len);
		const id = new Uint32Array(cap);
		id.set(this.id);
		const cls = new Uint8Array(cap);
		cls.set(this.cls);
		this.start = start;
		this.len = len;
		this.id = id;
		this.cls = cls;
		this.cap = cap;
	}

	push(start: number, len: number, id: number, cls: number): void {
		const n = this.count;
		if (n === this.cap) this.grow();
		this.start[n] = start;
		this.len[n] = len;
		this.id[n] = id;
		this.cls[n] = cls;
		this.count = n + 1;
	}

	intern(folded: string): number {
		let id = this.map.get(folded);
		if (id === undefined) {
			id = this.vocab.length;
			this.vocab.push(folded);
			this.map.set(folded, id);
		}
		return id;
	}

	fold(id: number): string {
		return this.vocab[id] ?? '';
	}

	lookup(folded: string): number {
		return this.map.get(folded) ?? -1;
	}

	/** Trimmed, immutable-by-convention view for `Doc.tokens`. */
	finish(): Tokens {
		const n = this.count;
		const vocab = this.vocab;
		const map = this.map;
		return {
			count: n,
			start: this.start.slice(0, n),
			len: this.len.slice(0, n),
			id: this.id.slice(0, n),
			cls: this.cls.slice(0, n),
			fold: (id: number) => vocab[id] ?? '',
			lookup: (folded: string) => map.get(folded) ?? -1,
			vocabSize: vocab.length,
		};
	}
}
